'use client';

import {
  type ChangeEvent,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
} from 'react';
import { clsx } from 'clsx';
import { useMultipleSelection, useSelect } from 'downshift';
import SvgIconClear from '../../../icons/react/icon-clear';
import DisclosureDownIcon from '../../../icons/react/icon-disclosure-down';
import DisclosureUpIcon from '../../../icons/react/icon-disclosure-up';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import { VerticalStack } from '../../alignment/vertical-stack';
import { PlainIconButton } from '../../button';
import { RemovablePill } from '../../pill/removable-pill';
import { Typography } from '../../typography';
import { ErrorMessage } from '../error-message/error-message';
import { InputHint } from '../input-hint/input-hint';
import { Label } from '../label/label';
import {
  type GetInputDropdownItemProps,
  InputDropdown,
  type InputDropdownGroupStyle,
  type InputDropdownOption,
  type InputDropdownOptionMultiple,
  type InputDropdownPagination,
  inputDropdownOptionToString,
  useFloatingDropdown,
} from '../shared/dropdown/input-dropdown';
import inputStyles from '../input.module.scss';
import styles from './multi-select-input.module.scss';

export type MultiSelectInputEvent<T extends string = string> = Omit<ChangeEvent<HTMLSelectElement>, 'target'> & {
  target: {
    value: T[];
    name?: string;
  };
};

type MultiSelectInputOnChange<T extends string = string> = (event: MultiSelectInputEvent<T>) => void;

function getSelectAllItem<T extends string>(
  hasAllSelected: boolean,
  selectAllLabel: string | undefined,
  allOptions: InputDropdownOption<T>[],
): InputDropdownOptionMultiple<T> | undefined {
  if (hasAllSelected || !selectAllLabel) {
    return undefined;
  }

  return {
    id: 'selectAll',
    group: 'actions',
    value: allOptions.map((option) => option.value),
    label: selectAllLabel,
  };
}

export interface MultiSelectInputProps<T extends string = string> extends Omit<HTMLAttributes<HTMLSelectElement>, 'onChange'> {
  selectAllLabel?: string;
  allowClear?: boolean;
  disabled?: boolean;
  error?: string;
  groupStyle?: InputDropdownGroupStyle;
  hint?: ReactNode;
  label: string;
  name: string;
  options: InputDropdownOption<T>[];
  onChange?: MultiSelectInputOnChange<T>;
  pagination?: InputDropdownPagination;
  placeholder?: string;
  ref?: Ref<HTMLSelectElement>;
  required?: boolean;
  touched?: boolean;
  value: T[];
}

export function MultiSelectInput({
  allowClear = true,
  id: providedId,
  disabled,
  error,
  groupStyle,
  hint,
  label,
  name,
  onChange,
  options,
  pagination,
  placeholder,
  ref,
  required,
  selectAllLabel,
  touched,
  value,
  ...rest
}: MultiSelectInputProps) {
  const selectedItems = useMemo(() => options.filter((option) => value.includes(option.value)), [options, value]);
  const optionsWithExtras = useMemo(() => {
    const selectAllItem = getSelectAllItem(value.length === options.length, selectAllLabel, options);
    return [...(selectAllItem ? [selectAllItem] : []), ...options];
  }, [options, value, selectAllLabel]);
  const itemsToRender = useMemo(
    () => optionsWithExtras.filter((option) => !selectedItems.find((selectedItem) => selectedItem.value === option.value)),
    [selectedItems, optionsWithExtras],
  );
  const { getSelectedItemProps, getDropdownProps, addSelectedItem, removeSelectedItem, reset, setSelectedItems } = useMultipleSelection({
    selectedItems,
    onStateChange: ({ selectedItems: newSelectedItems, type }) => {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.FunctionReset:
        case useMultipleSelection.stateChangeTypes.FunctionSetSelectedItems:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
        case useMultipleSelection.stateChangeTypes.FunctionAddSelectedItem: {
          if (onChange) {
            onChange({
              target: {
                name,
                value: (newSelectedItems || []).map((item) => item.value),
              },
            } as MultiSelectInputEvent);
          }

          break;
        }
      }
    },
  });
  const generatedId = useId();
  const id = providedId || generatedId;
  const labelId = `${id}-label`;
  const menuId = `${id}-menu`;
  const toggleButtonId = `${id}-toggle`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const showError = Boolean(touched && error && value);

  const { getItemProps, getMenuProps, getLabelProps, getToggleButtonProps, highlightedIndex, isOpen } = useSelect({
    id,
    menuId,
    labelId,
    toggleButtonId,
    items: itemsToRender,
    itemToString: inputDropdownOptionToString,
    selectedItem: null,
    defaultHighlightedIndex: 0,
    stateReducer: (_, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: 0,
          };
      }
      return changes;
    },
    onStateChange: ({ type, selectedItem: newSelectedItem }) => {
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
        case useSelect.stateChangeTypes.ToggleButtonBlur:
          if (newSelectedItem) {
            if (Array.isArray(newSelectedItem.value)) {
              setSelectedItems(options.filter((opt) => newSelectedItem.value.includes(opt.value)));
            } else {
              addSelectedItem(newSelectedItem as InputDropdownOption);
            }
          }
          break;
        default:
          break;
      }
    },
  });

  const { refs, floatingStyles } = useFloatingDropdown(options.length, isOpen);

  const listboxProps = {
    ...getMenuProps({ ref: refs.setFloating }),
    style: floatingStyles,
    hidden: !isOpen,
  };

  useImperativeHandle(
    ref,
    () =>
      ({
        id,
        value,
        name,
        ...refs.reference,
      }) as unknown as HTMLSelectElement,
    [id, name, refs.reference, value],
  );

  const hasValue = Boolean(selectedItems.length || placeholder);
  const isFloating = Boolean(hasValue || isOpen);

  const handleReset = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      reset();
    },
    [reset],
  );

  return (
    <VerticalStack
      className={clsx(
        inputStyles.input,
        styles.multiSelectInput,
        hasValue && styles.hasValue,
        isFloating && styles.isFloating,
        disabled && inputStyles.isDisabled,
        disabled && styles.isDisabled,
      )}
    >
      <VerticalStack ref={refs.setReference}>
        <HorizontalStack className={clsx(inputStyles.activator, styles.triggerContainer, styles.inputElement)}>
          <Label
            {...getLabelProps({ label, className: clsx(styles.label, isFloating && styles.isFloating) })}
            hasError={showError}
            isFloating={isFloating}
            required={required}
          />

          <HorizontalStack align="space-between" blockAlign="center" className={styles.triggerSelection} wrap="nowrap">
            <HorizontalStack blockAlign="center" className={styles.selectionRail} gap={2} wrap="wrap">
              {selectedItems.map((selectedItem, index) => (
                <RemovablePill
                  key={Array.isArray(selectedItem.value) ? selectedItem.value.join('-') : selectedItem.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSelectedItem(selectedItem);
                  }}
                  size="medium"
                >
                  <HorizontalStack
                    align="start"
                    blockAlign="center"
                    className={styles.item}
                    gap={2}
                    wrap="nowrap"
                    {...getSelectedItemProps({ selectedItem, index })}
                  >
                    {selectedItem.prefix ? <span className={styles.prefix}>{selectedItem.prefix}</span> : null}
                    <Typography>{selectedItem.pillLabel ?? selectedItem.label}</Typography>
                  </HorizontalStack>
                </RemovablePill>
              ))}
            </HorizontalStack>

            <HorizontalStack
              aria-describedby={hint ? hintId : undefined}
              aria-errormessage={showError ? errorId : undefined}
              className={clsx(styles.iconContainer, showError && styles.error)}
              {...getToggleButtonProps({
                ...getDropdownProps({ preventKeyAction: isOpen }),
                ...(rest as unknown as HTMLAttributes<HTMLDivElement>),
                disabled,
                required,
              })}
              align="end"
              blockAlign="center"
              gap={4}
              wrap="nowrap"
            >
              {allowClear && value?.length ? (
                <PlainIconButton aria-label="Clear selection" className={styles.clearButton} onClick={handleReset}>
                  <SvgIconClear aria-hidden width={16} />
                </PlainIconButton>
              ) : null}

              <PlainIconButton>
                {isOpen ? (
                  <DisclosureUpIcon aria-hidden className={styles.toggleIcon} />
                ) : (
                  <DisclosureDownIcon aria-hidden className={styles.toggleIcon} />
                )}
              </PlainIconButton>
            </HorizontalStack>
          </HorizontalStack>
        </HorizontalStack>

        {hint ? (
          <InputHint className={styles.inputElement} id={hintId}>
            {hint}
          </InputHint>
        ) : null}
        {showError ? (
          <ErrorMessage className={styles.inputElement} id={errorId}>
            {error}
          </ErrorMessage>
        ) : null}
      </VerticalStack>

      <InputDropdown
        getItemProps={getItemProps as GetInputDropdownItemProps}
        groupStyle={(groupStyle ?? selectAllLabel) ? 'border' : undefined}
        highlightedIndex={highlightedIndex}
        inputId={id}
        noOptionsMessage={!itemsToRender.length && options.length ? 'No more options' : 'No options'}
        options={itemsToRender}
        pagination={pagination}
        selectedItemValue={selectedItems.map((item) => item.value)}
        {...(listboxProps as HTMLAttributes<HTMLDivElement>)}
      />
    </VerticalStack>
  );
}
